/**
 * flowFieldHelp — zentrale, DEUTSCHE Erklärungen für den AI-Flows-Editor.
 *
 * Bewusst auf Deutsch (interne Admin-Tool-Ausnahme von der sonst englischen
 * Insights-UI): das Team ist deutschsprachig und versteht die Flow-Mechanik so
 * eindeutig. Kein i18n-System — hartkodierte Konstanten, leicht zu pflegen.
 *
 * Jeder Eintrag: { title, items: [{ q?, p }] }
 *   - `title`  Überschrift im Popover
 *   - `items`  Absätze; `q` (optional) ist ein fett gesetzter Frage-/Stichwort-
 *              Kopf, `p` der Erklärtext. Reine Absätze lassen `q` weg.
 *
 * Gerendert über <FlowHelp k="…" /> (nutzt die geteilte InfoButton-Komponente).
 * Zwei Ebenen: SEKTIONS-Erklärungen (Überblick) + FELD-Erklärungen (Details der
 * erklärungsbedürftigen Einzelfelder).
 */

export const FLOW_HELP = {
  // ---- Sektionen ---------------------------------------------------------
  identity: {
    title: 'Identity — Name & Kennung des Flows',
    items: [
      { p: 'Grunddaten dieses Auswahl-Flows (einer „Branche" wie eBike, Power Tool).' },
      { q: 'Branch label', p: 'Der Anzeigename, den Kunden sehen (auf der Cell-Selection-Startseite als Chip). Frei änderbar.' },
      { q: 'Slug', p: 'Die interne, permanente Kennung (z.B. „ebike"). Kann nach dem Anlegen NICHT mehr geändert werden — sie verknüpft Flow, Knowledge-Einträge und Deep-Links.' },
      { q: 'Enabled', p: 'Häkchen an = der Flow ist für Kunden sichtbar und Ion kann ihn nutzen. Aus = versteckt (Zeile bleibt erhalten, gut zum Zurückziehen ohne Löschen).' },
    ],
  },
  intro: {
    title: 'Intro — die erste Nachricht an den Kunden',
    items: [
      { p: 'Markdown-Text, den Ion dem Kunden zu Beginn des Flows WORTWÖRTLICH zeigt — bevor die erste Frage kommt. Ideal, um den Ablauf zu erklären („ich frage erst deine Anforderungen, dann gewichte ich…").' },
      { q: 'Wichtig', p: 'Der Text wird deterministisch ausgespielt (nicht von der KI umformuliert). Was du hier schreibst, sieht der Kunde exakt so.' },
    ],
  },
  hardConstraints: {
    title: 'Hard constraints — die Muss-Filter',
    items: [
      { p: 'Harte Gates, die Zellen AUSSCHLIESSEN. Ion schlägt sie vor und filtert erst, nachdem der Kunde sie bestätigt hat — ein vorgeschlagenes Gate filtert nie von allein.' },
      { q: 'comparison', p: 'Operator (≥ ≤ = > <) + Wert + Einheit, z.B. „Dauerstrom ≥ 20 A".' },
      { q: 'allow-list', p: 'Erlaubte kategoriale Werte, kommagetrennt, z.B. Format „18650, 21700".' },
      { q: 'range', p: 'Min / Max + Einheit.' },
      { q: 'Faustregel', p: 'Wenige und sinnvolle Gates. Keine Leistungszahlen in den Fragetext schreiben — der Plot zeigt die echten Messwerte.' },
    ],
  },
  scoringWeights: {
    title: 'Scoring weights — wie die Überlebenden gerankt werden',
    items: [
      { p: 'Bestimmt die Reihenfolge der Zellen, die durch die Hard constraints kamen. Jede Metrik hat ein Gewicht 0–1.' },
      { q: 'Slider / Σ', p: 'Der Σ-Wert zeigt die Summe. „Normalize" skaliert auf 1,0 (der Ranker normalisiert ohnehin, aber ≈1,0 ist lesbarer).' },
      { q: 'lower = better', p: 'Ankreuzen bei Metriken, wo KLEINER besser ist (Masse, Volumen, Innenwiderstand). Siehe eigenes Info-Icon am Häkchen.' },
    ],
  },
  plotAxes: {
    title: 'Plot axes — die Ragone-Achsen',
    items: [
      { p: 'Die zwei Metriken, auf denen Ion die Kandidaten im Live-Plot aufträgt. Konvention: X = Leistung, Y = Energie.' },
    ],
  },
  mustAsk: {
    title: 'Must-ask questions — die Schritte des Flows',
    items: [
      { p: 'Die Fragen, die Ion der Reihe nach stellt (die „Steps"). Jede hat einen Slug (interne ID), den Fragetext, optionale Antwort-Optionen und die Eingabe-Art.' },
      { q: 'Reihenfolge', p: 'Mit ▲ / ▼ sortieren. Ion fragt genau EINE Frage pro Runde und arbeitet sie in dieser Reihenfolge ab.' },
      { q: 'phase / input', p: 'Siehe die eigenen Info-Icons an diesen Feldern — sie steuern, ob eine Frage filtert, sized oder gewichtet, und welches Eingabe-Widget erscheint.' },
    ],
  },
  weightingQuestions: {
    title: 'Weighting questions — Trade-off-Fragen',
    items: [
      { p: 'Fragen, die NIE eine Zelle ausschließen — sie verschieben nur das Ranking (z.B. „Reichweite oder leichtes Pack?"). Im Gegensatz zu Must-ask (filtern/sizen).' },
      { q: 'Aufbau', p: 'Slug + Fragetext + Optionen + eine „Maps to"-Tabelle (welche Metrik um wie viel verschoben wird) + eine Pflicht-Erklärung, die der Kunde sieht.' },
      { q: 'Maps to', p: 'Der Kern — siehe das eigene Info-Icon an „Maps to".' },
    ],
  },
  weightRules: {
    title: 'Weight rules (advanced) — exakte Gewichts-Regeln pro Antwort',
    items: [
      { p: 'JSON-Liste von Regeln „WENN Antwort X, DANN ändere Gewicht Y um Z". Präziser als „Maps to": hier legst du pro konkreter Antwort-Option ein exaktes Delta fest, statt die KI interpretieren zu lassen.' },
      { q: 'Aufbau einer Regel', p: '{ "when": {"question": "<slug>", "answer": "<value>"}, "adjust": {"<metrik>": <delta>}, "reason": "…", "bounds": {"<metrik>": {"min":…, "max":…}} }. „when.answer" muss dem VALUE der Option entsprechen (nicht dem Label).' },
      { q: 'bounds', p: 'Optionaler Klammer-Bereich, damit eine Regel ein Gewicht nicht aus dem sinnvollen Bereich schiebt.' },
      { q: 'Maps to vs. Weight rules', p: '„Maps to" = grobe Richtung der Frage (KI interpretiert). „Weight rules" = feste Wirkung pro Antwort (kein Raten). Für exaktes, reproduzierbares Verhalten die Weight rules nutzen.' },
    ],
  },
  crossCutting: {
    title: 'Cross-cutting questions — Rahmenfragen',
    items: [
      { p: 'Fragen, die über den ganzen Flow gelten und NICHT ins Ranking einfließen — Beschaffungsregion, Budget, Projektlaufzeit. „Gut zu wissen", ändert aber weder Filter noch Reihenfolge.' },
      { q: 'Wirkung', p: 'Ion stellt sie an einer natürlichen Stelle einmal und hält damit nie die Shortlist auf. Rein informativ für den späteren Report / die Beratung.' },
    ],
  },
  systemRequirements: {
    title: 'System requirements — Pack-Mathematik verdrahten',
    items: [
      { p: 'Verbindet eine Anforderungs-Frage mit der deterministischen Pack-Rechnung. Beispiel: die „Pack-Spannung"-Frage speist das Feld pack_voltage_v.' },
      { q: 'Enable pack-sizing gate', p: 'Häkchen an = Ion prüft die Pack-Machbarkeit (nicht nur Einzelzell-Fit) gegen die genannten Anforderungen.' },
      { q: 'Mapping (Frage-Slug → Feld)', p: 'Siehe das eigene Info-Icon — welches Feld welche Anforderung füllt.' },
    ],
  },
  defaults: {
    title: 'Defaults — Annahmen ohne Kundenangabe',
    items: [
      { p: 'Werte, die Ion nutzt, wenn der Kunde sie nicht angibt. Ein „schlüssel: wert" pro Zeile.' },
      { q: 'Beispiele', p: 'ambient_c: 25 (Umgebungstemperatur in °C) · energy_per_distance_wh_km: 10 (Verbrauchs-Platzhalter für die Reichweitenschätzung).' },
    ],
  },
  disclaimers: {
    title: 'Disclaimers — Ehrlichkeits-Hinweise',
    items: [
      { p: 'Ein Hinweis pro Zeile. PFLICHT bei Branchen, die auf Proxy-Metriken ranken oder Datenlücken haben (z.B. wenn Zyklenlebensdauer nicht im Katalog ist). Erscheinen als Fußnote beim Kunden.' },
    ],
  },
  reportDefaults: {
    title: 'Report defaults — vorausgewählte Report-Abschnitte',
    items: [
      { p: 'Welche der acht Report-Abschnitte beim Öffnen des Supervisor-Reports für diesen Flow vorangehakt sind (Executive summary, Application profile, Ranked shortlist, Ragone plot, Provenance, Per-cell rationale, Pack sizing, Next steps).' },
      { q: 'Nur Startzustand', p: 'Der Mitarbeiter kann vor dem Export jeden Abschnitt an- oder abwählen — das hier setzt nur die sinnvolle Vorauswahl je Anwendungsfall.' },
    ],
  },
  knowledge: {
    title: 'Knowledge (flow-specific) — Wissen nur für diese Branche',
    items: [
      { p: 'Knowledge-Einträge, die mit dem Slug dieser Branche getaggt sind. Ion priorisiert sie in seinem Wissens-Index, sobald ein Kunde in diesem Flow ist — für branchenspezifische Faustregeln, Fallstricke, Trade-off-Erklärungen.' },
      { q: 'Abruf', p: 'Der Volltext wird bei Bedarf über das Tool get_knowledge geladen; globales Wissen bleibt erreichbar, nur niedriger priorisiert.' },
    ],
  },

  // ---- Erklärungsbedürftige Einzelfelder --------------------------------
  mapsTo: {
    title: 'Maps to (metric → weight delta)',
    items: [
      { q: 'Wieso stehen da Werte?', p: 'Es sind Gewichts-Deltas — um wie viel eine Metrik im Ranking steigt (+) oder fällt (−). Vorzeichen = Richtung, Betrag = Stärke (0,1 = spürbar, 0,05 = schwach).' },
      { q: 'Bei mehreren Antworten — welcher Wert gilt?', p: '„Maps to" ist EINE Angabe für die GANZE Frage, nicht pro Antwort. Es beschreibt die „starke" Richtung. Wie stark eine einzelne Antwort wirkt (voll / neutral / umgekehrt), entscheidet die KI. Für exaktes Verhalten pro Antwort nutzt du die „Weight rules".' },
      { q: 'Wie wirken sie?', p: 'Das Gewicht der Metrik wird um den Delta verschoben; danach rechnet der Ranker alle Gewichte wieder auf Summe 1 (Normalisierung).' },
      { q: 'Wie berechnet die KI das?', p: 'Start = Flow-Gewichte → dann Weight-rules-Deltas (fest pro Antwort) + Maps-to-Deltas (je nach Antwort) addieren → „kleiner ist besser"-Metriken kommen ins minimize-Set. Deltas klein halten, es wird ohnehin normalisiert.' },
    ],
  },
  phase: {
    title: 'phase — welche Rolle die Frage spielt',
    items: [
      { q: 'reduction', p: 'Verkleinert das Feld (harte Filter: Format, Chemie, Zertifizierung). Wird zu einem Hard constraint.' },
      { q: 'requirement', p: 'Eine System-Größe (Pack-Spannung, Leistung, Reichweite, Parallelzahl). Fließt in die Pack-Mathematik.' },
      { q: 'weighting', p: 'Eine Präferenz, die das Ranking verschiebt — filtert nicht.' },
      { p: 'Ion arbeitet die Phasen in dieser Reihenfolge ab: reduction → requirement → weighting.' },
    ],
  },
  inputType: {
    title: 'input — das Eingabe-Widget der Frage',
    items: [
      { q: 'choice', p: 'Auswahl-Buttons aus den definierten Optionen (+ optional Freitext).' },
      { q: 'number', p: 'Zahleneingabe mit Einheit und optional min/max (z.B. Spannung in V).' },
      { q: 'topology', p: 'Serien×Parallel-Picker (s und p). Hinweis: Für „nur Parallelzahl fragen" nutzt du stattdessen eine number/choice-Frage, die auf das Feld parallel_count mappt — dann rechnet der Code die Seriezahl aus Spannung + Chemie.' },
    ],
  },
  lowerBetter: {
    title: 'lower = better',
    items: [
      { p: 'Ankreuzen, wenn bei dieser Metrik ein KLEINERER Wert besser ist — z.B. Zellmasse, Volumen, Innenwiderstand (DCIR).' },
      { q: 'Warum wichtig', p: 'Der Ranker geht sonst von „größer = besser" aus. Das Häkchen nimmt die Metrik ins minimize-Set, damit kleine Werte oben ranken. Das Gewicht selbst bleibt „wie wichtig", das Häkchen ist die Richtung.' },
    ],
  },
  questionSlug: {
    title: 'slug — die interne Frage-ID',
    items: [
      { p: 'Kurze, eindeutige Kennung der Frage (z.B. „target_range"). Kleinbuchstaben, snake_case.' },
      { q: 'Wofür', p: 'Verknüpft die Antwort mit der Pack-Mathematik (über das System-requirements-Mapping) und den Weight rules („when.question"). Ion gibt den Slug bei jeder Frage mit, damit der Fortschritt sauber abgehakt wird und keine Frage doppelt kommt.' },
    ],
  },
  systemReqMapping: {
    title: 'Mapping: Frage-Slug → Requirement-Feld',
    items: [
      { p: 'Links der Slug einer requirement-Frage, rechts das Pack-Mathe-Feld, das ihre Antwort füllt.' },
      { q: 'Die Felder', p: 'pack_voltage_v (Pack-Nennspannung) · pack_peak_power_w / pack_cont_power_w (Spitzen-/Dauerleistung) · pack_energy_wh (Pack-Energie) · range_km (Reichweite) · max_weight_kg / max_volume_l (Budgets) · topology (s+p) · parallel_count (nur p — die Seriezahl leitet der Code aus Spannung + Zellchemie ab).' },
    ],
  },
}
